/* eslint-disable no-unused-expressions */
import { getAuth } from 'firebase/auth';
import { User } from 'hydra-data-types';
import { Unsubscribe } from 'redux';
import { ApiClient } from '../api/ApiClient';
import { StoreType } from '../store/Store';
import { ActionType } from '../store/StoreTypes';

type Callback = () => any;

export class AppStackInitializer {
  private readonly store: StoreType;

  private resolve?: Callback;

  private reject?: (reason?: unknown) => void;

  private authUnsubscribe?: Callback;

  private storeUnsubscribe?: Unsubscribe;

  constructor(store: StoreType) {
    this.store = store;

    this.succeedIfPossible = this.succeedIfPossible.bind(this);
  }

  async initialize() {
    return new Promise<void>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
      this.tryInitialization();
    });
  }

  private async tryInitialization() {
    try {
      if (this.canSucceed()) {
        this.succeed();
      } else {
        this.authUnsubscribe = getAuth().onAuthStateChanged(this.succeedIfPossible);
        this.storeUnsubscribe = this.store.subscribe(this.succeedIfPossible);
      }

      // Fetch from the database. Do this even if we have already called onSuccess in order to update
      // the locally persisted data in the store.
      const { user } = await new ApiClient().initialize();

      this.store.dispatch({ type: ActionType.SET_USER, user });
      // Don't need to call success here, will be handled by the store change subscription
    } catch (e) {
      console.warn(`AppStackInitializer: Error during initialization: ${e}`);
      // If the API initialize call fails, e.g. no network connection, then we still have a good change
      // of being able to continue based on persisted state.
      let success = false;
      try {
        success = this.canSucceed();
      } finally {
        console.warn(`AppStackInitializer: ${success ? 'Able to proceed' : 'Unable to proceed'}`);

        if (success) {
          this.resolve?.();
        } else {
          this.reject?.(e);
        }
        this.teardown();
      }
    }
  }

  private canSucceed(): boolean {
    const { user: storeUser } = this.store.getState();
    this.assertStoreUser(storeUser);
    return storeUser !== undefined;
  }

  private assertStoreUser(storeUser: User | undefined) {
    const authUser = getAuth().currentUser;
    if (storeUser && authUser && storeUser.userId !== authUser.uid) {
      // Should never happen. Maybe if switching between Firebase environments
      this.store.dispatch({ type: ActionType.CLEAR_USER });
      throw new Error(
        `Store user ${storeUser.userId} does not match authorised user ${authUser.uid}`
      );
    }
  }

  private succeedIfPossible() {
    if (this.canSucceed()) {
      this.succeed();
    }
  }

  private succeed() {
    this.resolve?.();
    this.teardown();
  }

  private teardown() {
    this.resolve = undefined;
    this.reject = undefined;

    this.authUnsubscribe?.();
    this.authUnsubscribe = undefined;

    this.storeUnsubscribe?.();
    this.storeUnsubscribe = undefined;
  }
}
